import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
    tryToCreateSuppliers,
    tryToEditSuppliers,
    tryToFetchSingleSuppliers
} from '../../../store/brokers/admin/suppliers/SuppliersSlice';
import Input from '../../../shared/input';
import Error from '../../../shared/error';
import Button from '../../../shared/button';
import SelectCheckbox from '../../../shared/select-checkbox';


type FormValues = {
    name: string;
    email: string;
    username: string;
    password: string;
    country: string;
    confirmPassword: string;
    url?: string;
};

const CreateSupplier = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const [createError, setCreateError] = useState<string | null>()
    const supplier = useAppSelector((state) => state?.suppliers?.supplier);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
    } = useForm<FormValues>({});

    useEffect(()=>{
        if(supplier && id){
            reset(supplier)
        }
    },[supplier, id])

    const getSupplierDetailsData = async () => {
        if (id) {
            dispatch(tryToFetchSingleSuppliers(id)).unwrap()
        } else {
            setCreateError(undefined)
            reset()
        }
    }

    useEffect(() => {
        getSupplierDetailsData()
    }, [])

    useEffect(() => {
        getSupplierDetailsData()
    }, [id])

    const onSubmit = async (data: FormValues) => {
        try {
            if (id) {
                const formatData = {
                    name: data?.name,
                    country: data?.country
                }
                await dispatch(tryToEditSuppliers({ id: id || '', data: formatData })).unwrap();
            } else {
                await dispatch(tryToCreateSuppliers(data)).unwrap();
            }
            reset();
            navigate('/suppliers');
            setCreateError(null)
        } catch (error) {
            setCreateError(`${error}`)
        }
    };

    const onGoBack = () => {
        navigate('/suppliers?type=suppliers')
    }

    return (
        <div>
            <div className={'flex flex-row items-center justify-between'}>
                <div className='flex flex-row items-center'>
                    <div data-qa={'back-button'} onClick={onGoBack} className='md:-ml-2 xl:-ml-8 md:mr-2 xl:mr-10'>
                        <img src={'/assets/shared/go-back.svg'} className={'w-[18px] object-contain cursor-pointer'} />
                    </div>
                    <p className='page-title'>{id ? 'Edit Supplier' : 'Create Supplier'}</p>
                </div>
            </div>
            <div className='mt-5 mb-2'>
                {createError && <Error text={createError} />}
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='grid grid-cols-3 gap-4 content-center'>
                        <Input
                            label='Name'
                            dataQa={'create-supplier-name'}
                            register={register('name', {
                                required: {
                                    message: 'Name is required',
                                    value: true,
                                },
                                validate: (value: string) => !!value.trim() || 'Name is required'
                            })}
                            error={errors.name?.message}
                        />
                        <Input
                            label='Username'
                            dataQa={'create-supplier-username'}
                            disabled={!!id}
                            register={register('username', {
                                required: {
                                    message: 'Username is required',
                                    value: true,
                                },
                                pattern: {
                                    /* eslint-disable-next-line */
                                    value: /^\S*$/,
                                    message: 'Username should not include spaces'
                                },
                                validate: (value: string) => !!value.trim() || 'Username is required'
                            })}
                            error={errors.username?.message}
                        />
                        <Input
                            label='Email'
                            dataQa={'create-supplier-email'}
                            disabled={!!id}
                            register={register('email', {
                                required: {
                                    message: 'Email is required',
                                    value: true,
                                },
                                pattern: {
                                    /* eslint-disable-next-line */
                                    value: /^(([^<>()[\]\\.,;:\s@\']+(\.[^<>()[\]\\.,;:\s@\']+)*)|(\'.+\'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                    message: 'Please write a valid email'
                                },
                                validate: (value: string) => !!value.trim() || 'Email is required'
                            })}
                            error={errors.email?.message}
                        />
                        <Input
                            label='Password'
                            dataQa={'create-supplier-password'}
                            disabled={!!id}
                            type={'password'}
                            register={register('password', {
                                required: {
                                    message: 'Password is required',
                                    value: !id,
                                },
                                minLength: {
                                    value: 8,
                                    message: 'Minimum number of characters is 8'
                                },
                                pattern: {
                                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                                    message: 'Password should be at least 8 characters long, and must contain at least 1 number, 1 lower case, 1 upper case and 1 special character(@$!%*?&)'
                                },
                                validate: (value: string) => id ? true : !!value.trim() || 'Password is required and min length is 8'
                            },
                            )}
                            error={errors.password?.message}
                        />
                        <Input
                            label='Repeat Password'
                            dataQa={'create-supplier-repeat-password'}
                            disabled={!!id}
                            type={'password'}
                            register={register('confirmPassword', {
                                required: {
                                    message: 'Repeat password is required',
                                    value: !id,
                                },
                                validate: (val: string) => {
                                    if (watch('password') !== val) {
                                        return 'Repeat password do not match';
                                    }
                                }
                            })}
                            error={errors.confirmPassword?.message}
                        />
                        <Input
                            label='Country'
                            dataQa={'create-supplier-country'}
                            register={register('country', {
                                required: {
                                    message: 'Country is required',
                                    value: true,
                                },
                                validate: (value: string) => !!value.trim() || 'Country is required'
                            })}
                            error={errors.country?.message}
                        />
                    </div>
                    <div className='flex flex-row justify-end py-2'>
                        <Button
                            label={id ? 'Save Changes' : 'Create Supplier'}
                            type={'submit'}
                            className={'btn-primary min-w-[250px]'}
                            dataQa={'create-supplier-button'}
                        />
                    </div>
                </form>
            </div>
        </div >
    )
}

export default CreateSupplier;